import React, { useState, useEffect } from 'react';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { PageLayout } from '@src/common/PageLayout';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { FileUpload as FileUploadComp } from '@src/common/FileUploadV2';
import { Card } from '@uhg-abyss/web/ui/Card';
import { StateSelect } from '../Inputs/StateSelect/StateSelect';
import { ZipCode } from '../Inputs/ZipCode';
import { City } from '../Inputs/City';
import { StreetLine } from '../Inputs/StreetLine';

const Upload = styled('div', {
  '& [data-testid*="remove-icon"]': {
    marginLeft: '15%',
  },
});

const AlignZip = styled('div', {
  marginLeft: '10px',
});

const Space = styled('div', {
  marginBottom: '16px',
});

const Text = styled('p', {
  lineHeight: '22px',
});

const AlignCheckBox = styled('div', {
  marginBottom: '8px',
});

export const PrimaryRemittance = () => {
  const form = useForm();

  const primaryServicingAddress = form.getValues('address.primaryServicing');
  const primaryBillingAddress = form.getValues('address.primaryBilling');

  const isRemittanceSameAsServicing = form.getValues(
    'primaryRemittanceAddress.isSameAsServicing'
  );
  const isRemittanceSameAsBilling = form.getValues(
    'primaryRemittanceAddress.isSameAsBilling'
  );
  const primaryRemittanceAddressProof = form.getValues(
    'address.primaryRemittanceProof'
  );

  const [isServicingCheckboxDisabled, setServicingCheckboxDisabled] =
    useState(true);
  const [isBillingCheckboxDisabled, setBillingCheckboxDisabled] =
    useState(true);

  const clearRemittanceProof = () => {
    form.setValue('address.primaryRemittanceProof', undefined, {
      errors: { required: true },
      touched: true,
    });
  };

  const handleServicingAddressChange = () => {
    if (!isRemittanceSameAsServicing) {
      form.setValue('address.primaryRemittance', primaryServicingAddress);
      form.setValue('primaryRemittanceAddress.isSameAsBilling', false);
      if (!primaryRemittanceAddressProof) {
        clearRemittanceProof();
      }
    } else {
      form.setValue('address.primaryRemittance', undefined);
      clearRemittanceProof();
    }
  };

  const handleBillingAddressChange = () => {
    if (!isRemittanceSameAsBilling) {
      form.setValue('address.primaryRemittance', primaryBillingAddress);
      form.setValue('primaryRemittanceAddress.isSameAsServicing', false);
      if (!primaryRemittanceAddressProof) {
        clearRemittanceProof();
      }
    } else {
      form.setValue('address.primaryRemittance', undefined);
      clearRemittanceProof();
    }
  };

  useEffect(() => {
    if (
      primaryBillingAddress &&
      primaryBillingAddress.city !== '' &&
      primaryBillingAddress.streetLine1 !== '' &&
      primaryBillingAddress.zipCode !== '' &&
      primaryBillingAddress.state &&
      primaryBillingAddress.state.value !== null
    ) {
      setBillingCheckboxDisabled(false);
    } else {
      setBillingCheckboxDisabled(true);
    }
    if (
      primaryServicingAddress &&
      primaryServicingAddress.city !== '' &&
      primaryServicingAddress.streetLine1 !== '' &&
      primaryServicingAddress.zipCode !== '' &&
      primaryServicingAddress.state &&
      primaryServicingAddress.state.value !== null
    ) {
      setServicingCheckboxDisabled(false);
    } else {
      setServicingCheckboxDisabled(true);
    }
  }, [
    JSON.stringify(primaryBillingAddress),
    JSON.stringify(primaryServicingAddress),
  ]);
  // TODO come up with a better way to handle checkbox enable/disable

  return (
    <PageLayout>
      <Card header="Primary remittance address">
        <Card.Section>
          <Text>Provide your practice's primary remittance address.</Text>
          <Grid.Col span="80%" css={{ padding: '0px' }}>
            <AlignCheckBox>
              <Checkbox
                id="primary-remittance-address-servicing-checkbox"
                model="primaryRemittanceAddress.isSameAsServicing"
                onChange={handleServicingAddressChange}
                isDisabled={isServicingCheckboxDisabled}
              >
                Same as servicing address
              </Checkbox>
            </AlignCheckBox>
          </Grid.Col>
          <AlignCheckBox>
            <Checkbox
              id="primary-remittance-address-billing-checkbox"
              model="primaryRemittanceAddress.isSameAsBilling"
              onChange={handleBillingAddressChange}
              isDisabled={isBillingCheckboxDisabled}
            >
              Same as billing address
            </Checkbox>
          </AlignCheckBox>
          <Grid.Col span="80%" css={{ padding: '0px' }}>
            <Space>
              <StreetLine
                id="address-primary-remittance-street-line-1-input"
                model="address.primaryRemittance.streetLine1"
                label="Street line 1"
                streetLine="1"
                required
              />
            </Space>
          </Grid.Col>
          <Grid.Col span="80%" css={{ padding: '0px' }}>
            <Space>
              <StreetLine
                id="address-primary-remittance-street-line-2-input"
                model="address.primaryRemittance.streetLine2"
                label="Street line 2"
                streetLine="2"
                optional
              />
            </Space>
          </Grid.Col>
          <Grid.Col span="80%" css={{ padding: '0px' }}>
            <Space>
              <City
                id="primary-remittance-address-city-input"
                model="address.primaryRemittance.city"
                label="City"
                required
              />
            </Space>
          </Grid.Col>
          <Grid.Col span="80%" css={{ padding: '0px' }}>
            <Space>
              <Layout.Group>
                <StateSelect
                  model="address.primaryRemittance.state"
                  id="address-primary-remittance-state-select"
                  required
                />
                <AlignZip>
                  <ZipCode
                    id="primary-remittance-address-zipCode-input"
                    model="address.primaryRemittance.zipCode"
                    label="ZIP Code"
                    required
                  />
                </AlignZip>
              </Layout.Group>
            </Space>
          </Grid.Col>
          <Grid.Col span="100%" css={{ padding: '0px' }}>
            <Space>
              <Upload>
                <FileUploadComp
                  id="upload-primary-remittance-address-proof-button"
                  model="address.primaryRemittanceProof"
                  label="Proof of remittance address"
                  required
                />
              </Upload>
            </Space>
          </Grid.Col>
        </Card.Section>
      </Card>
    </PageLayout>
  );
};
